import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PropertyProvider } from "@/context/PropertyContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import SupervisorChecklist from "@/pages/SupervisorChecklist";
import ManagementDashboard from "@/pages/ManagementDashboard";
import InspectionsIndex from "@/pages/InspectionsIndex";
import ReportView from "@/pages/ReportView";
import AdminUsers from "@/pages/AdminUsers";
import ChecklistManager from "@/pages/ChecklistManager";
import "@/App.css";

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--linen)]">
        <div className="hotel-overline">Loading Tamanna Suite…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RoleRouter() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Login />;
  if (user.role === "supervisor") return <Navigate to="/inspect" replace />;
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<RoleRouter />} />
        <Route path="/login" element={<Login />} />
        <Route element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
          <Route path="/inspect" element={<ProtectedRoute roles={["supervisor"]}><SupervisorChecklist /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute roles={["owner", "management"]}><ManagementDashboard /></ProtectedRoute>} />
          <Route path="/inspections" element={<InspectionsIndex />} />
          <Route path="/inspections/:id" element={<ReportView />} />
          <Route path="/points" element={<ProtectedRoute roles={["owner", "management"]}><ChecklistManager /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute roles={["owner"]}><AdminUsers /></ProtectedRoute>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  useEffect(() => { document.title = "Tamanna Hotels • Housekeeping Checklist"; }, []);
  return (
    <PropertyProvider>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </PropertyProvider>
  );
}
